class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && this.close()
    );
    this.querySelector("#CartDrawer-Overlay").addEventListener(
      "click",
      this.close.bind(this)
    );
    this.setHeaderCartIconAccessibility();
    this.setupPromoProductRestrictions();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    if (!cartLink) return;

    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
      this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add("animate", "active");
    });

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.getElementById("CartDrawer");
        const focusElement =
          this.querySelector(".drawer__inner") ||
          this.querySelector(".drawer__close");
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add("overflow-hidden");
  }

  close() {
    this.classList.remove("active");
    removeTrapFocus(this.activeElement);
    document.body.classList.remove("overflow-hidden");
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute(
        "aria-controls",
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open")
      );
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector(".drawer__inner").classList.contains("is-empty") &&
      this.querySelector(".drawer__inner").classList.remove("is-empty");
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    setTimeout(() => {
      this.querySelector("#CartDrawer-Overlay").addEventListener(
        "click",
        this.close.bind(this)
      );
      this.setupPromoProductRestrictions(); // Re-setup restrictions after content update
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  setupPromoProductRestrictions() {
    // Prevent removal of promo products
    this.addEventListener("click", (event) => {
      const removeButton = event.target.closest(".cart-remove-button");
      if (removeButton) {
        const cartItem = removeButton.closest(".cart-item");
        const productName = cartItem.querySelector(".cart-item__name");

        if (productName && productName.textContent.includes("PROMO")) {
          event.preventDefault();
          event.stopPropagation();

          // Show a message to the user
          this.showPromoProductMessage(
            "Promo products cannot be removed from the cart."
          );
          return false;
        }
      }
    });

    // Prevent quantity changes for promo products
    this.addEventListener("click", (event) => {
      const quantityButton = event.target.closest(".quantity__button");
      if (quantityButton) {
        const cartItem = quantityButton.closest(".cart-item");
        const productName = cartItem.querySelector(".cart-item__name");

        if (productName && productName.textContent.includes("PROMO")) {
          event.preventDefault();
          event.stopPropagation();

          // Show a message to the user
          this.showPromoProductMessage(
            "Promo product quantity cannot be changed."
          );
          return false;
        }
      }
    });

    // Prevent manual input changes for promo products
    this.addEventListener("input", (event) => {
      if (event.target.classList.contains("quantity__input")) {
        const cartItem = event.target.closest(".cart-item");
        const productName = cartItem.querySelector(".cart-item__name");

        if (productName && productName.textContent.includes("PROMO")) {
          event.preventDefault();
          event.stopPropagation();

          // Restore original value
          event.target.value =
            event.target.getAttribute("data-original-value") || "1";

          // Show a message to the user
          this.showPromoProductMessage(
            "Promo product quantity cannot be changed."
          );
          return false;
        }
      }
    });

    // Store original values for promo products
    this.querySelectorAll(".cart-item").forEach((item) => {
      const productName = item.querySelector(".cart-item__name");
      const quantityInput = item.querySelector(".quantity__input");

      if (
        productName &&
        productName.textContent.includes("PROMO") &&
        quantityInput
      ) {
        quantityInput.setAttribute("data-original-value", quantityInput.value);
      }
    });

    // Check checkout restrictions
    this.updateCheckoutButton();
  }

  showPromoProductMessage(message) {
    // Create a temporary message element
    const messageEl = document.createElement("div");
    messageEl.className = "promo-product-message";
    messageEl.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #dc3545;
      color: white;
      padding: 12px 20px;
      border-radius: 4px;
      z-index: 10000;
      font-size: 14px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.2);
    `;
    messageEl.textContent = message;

    document.body.appendChild(messageEl);

    // Remove message after 3 seconds
    setTimeout(() => {
      if (messageEl.parentNode) {
        messageEl.parentNode.removeChild(messageEl);
      }
    }, 3000);
  }

  updateCheckoutButton() {
    const checkoutButton = this.querySelector("#CartDrawer-Checkout");
    if (!checkoutButton) return;

    const cartItems = this.querySelectorAll(".cart-item");
    let hasPromoProducts = false;
    let hasNonPromoProducts = false;

    cartItems.forEach((item) => {
      const productName = item.querySelector(".cart-item__name");
      if (productName) {
        if (productName.textContent.includes("PROMO")) {
          hasPromoProducts = true;
        } else {
          hasNonPromoProducts = true;
        }
      }
    });

    // Disable checkout if only promo products are in cart
    if (hasPromoProducts && !hasNonPromoProducts) {
      checkoutButton.disabled = true;
      checkoutButton.classList.add("button--disabled");
    } else {
      checkoutButton.disabled = false;
      checkoutButton.classList.remove("button--disabled");
    }
  }
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);
